import {
    DECISION_FIELDS_BY_PATH,
    OPERATORS_BY_VALUE,
    PREVIOUS_OUTCOME_FIELD,
} from '/b2b/dsarForms/routes/FormDetails/components/FormWorkflow/components/AddDecisionDialog/constants';
import {
    DSAR_ACTION_LABELS,
    DSAR_FIELD_TYPE_LABELS,
    DSAR_REQUEST_TYPE_AGENT_VERIFICATION_LEVEL_LABELS,
    DSAR_REQUEST_TYPE_GROUP_LABELS,
    DSAR_REQUEST_TYPE_JURISDICTION_LABELS,
    DSAR_REQUEST_TYPE_LABELS,
    DSAR_REQUEST_TYPE_VERIFICATION_LEVEL_LABELS,
} from '/b2b/dsarForms/constants';
import {
    NODE_TYPE_CONFIG,
    isBranchingNodeType,
} from '/b2b/dsarForms/routes/FormDetails/components/FormWorkflow/constants';

import { getCustomFieldLabelFromPath } from '/b2b/dsarForms/routes/FormDetails/components/FormWorkflow/components/AddDecisionDialog/helpers';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import palette from '/b2b/common/theme/palette';
import startCase from 'lodash/startCase';
import { stripBBCodeTags } from '/b2b/common/components/Locale/formatters/bbcodeTags';
import { translate } from '/b2b/common/helpers/i18n';
import unescape from 'lodash/unescape';

/** Anything that is not an object. Null and undefined count: both show as an em dash. */
const isScalar = value => value === null || value === undefined || typeof value !== 'object';

/**
 * A `{ label, value }` option that a dialog select stored as it is; only its label shows. Both keys
 * are necessary, so a decision rule `{ path, operator, value }` still shows as the sub-list it is.
 */
const isOptionObject = value =>
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'label' in value &&
    'value' in value &&
    isScalar(value.label);

/** A value that goes in one chip: a scalar, an option, or a list of either on one line. */
const isChipValue = value =>
    isScalar(value) ||
    isOptionObject(value) ||
    (Array.isArray(value) && value.every(item => isScalar(item) || isOptionObject(item)));

/**
 * An object one level deep, of which each value is a chip: actionConfig and automationConfig. An
 * option object is not one, because it is a single value.
 */
const isFlatObject = value =>
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !isOptionObject(value) &&
    Object.values(value).every(isChipValue);

/** Translates a key if the vocabulary has one, or gives the fallback. */
const translateOr = (key, fallback) => (key ? translate(key) : fallback);

/** The keys of both sides of a diff. A key that one side added or removed needs a row too. */
const unionKeys = (a, b) => [...new Set([...Object.keys(a), ...Object.keys(b)])];

// "New value" chip colors (osano-50 bg / osano-800 fg).
const CHANGE_NEW_VALUE_BG = '#F5F0FF';
const CHANGE_NEW_VALUE_FG = '#3E1A7F';

// Each style is inline, because the markup goes into a BBCode [html] block.
const { grey } = palette.common;
const OLD_CHIP = `background:${grey[100]};border-radius:4px;padding:1px 6px;text-decoration:line-through;text-decoration-color:${grey[400]};`;
const NEW_CHIP = `background:${CHANGE_NEW_VALUE_BG};color:${CHANGE_NEW_VALUE_FG};border-radius:4px;padding:1px 6px;`;
const ARROW = `color:${grey[500]};margin:0 10px;`;
const LIST_STYLE = 'list-style:none;padding-left:0;margin:4px 0;';
const NESTED_LIST_STYLE = 'list-style:none;padding-left:16px;margin:4px 0;';
const ITEM_STYLE = 'margin-bottom:6px;';

// Translation keys for known change paths, reusing the form editor's own field
// labels so the activity log matches what the user sees when editing.
//
// A workflow node names its own paths under `config.`, so they share this map
// rather than a second one — a node field cannot collide with a form path. A path
// the map does not know is start-cased, so only a field whose name the reader
// would not guess needs an entry.
export const PATH_LABEL_KEYS = {
    'settings.orgIds': 'common.organizations',
    'settings.emailIngestionConfig.allowListEmails': 'dsar.emailSettings.allowListEmails.label',
    'settings.emailIngestionConfig.escalationEmails': 'dsar.emailSettings.escalationEmails.label',
    'settings.aiEmailParsing': 'dsar.emailSettings.aiEmailParsing.label',
    'settings.captchaEnabled': 'dsar.formSettings.captchaEnabled',
    'settings.duplicateReject': 'dsar.formSettings.duplicateReject',
    'settings.fromCompany': 'dsar.formSettings.companyName',
    'settings.fromName': 'dsar.formSettings.displayName',
    'settings.internalDueDays': 'dsar.formSettings.internalDueDate',
    'settings.ssoConfigId': 'dsar.formSettings.consumerSso.provider',
    'settings.ssoRequired': 'dsar.formSettings.consumerSso.required',
    'settings.unverifiedEmailReject': 'dsar.formSettings.unverifiedEmailReject',
    'styling.showHeader': 'dsar.formSettings.showFormName',
    'styling.stylesheetURL': 'common.fields.stylesheetUrl.label',
    'styling.instructions': 'common.instructions',
    /* Workflow node fields */
    'config.name': 'common.name',
    'config.assignees': 'workflows.audit.fields.assignees',
    'config.description': 'common.fields.description.label',
    'config.dataStoreId': 'dsar.formDetails.actions.dialog.associateDataStore',
    'config.actionConfig': 'workflows.audit.fields.actionConfig',
    'config.automationConfig': 'workflows.audit.fields.automationConfig',
    'config.connectionId': 'dsar.formDetails.processing.fields.integration',
    'config.connectionName': 'dsar.formDetails.processing.fields.integration',
    'config.externalDisplayName': 'workflows.audit.fields.externalDisplayName',
    'config.subprocessors': 'dsar.formDetails.notifyVendor.label',
    'config.rejectionReason': 'workflows.audit.fields.rejectionReason',
    'config.completionMessage': 'workflows.audit.fields.completionMessage',
    'config.notifyEmail': 'workflows.audit.fields.notifyEmail',
    'config.path': 'dsarWorkflows.decision.field.label',
    'config.branches': 'workflows.audit.fields.branches',
};

// Palette colours reuse the styling editor's colour labels. Some palette keys
// differ from their schema key (requiredColor → asterisk, formBackgroundColor →
// background), so they are mapped explicitly.
const PALETTE_LABEL_KEYS = {
    primaryColor: 'dsar.formStyling.schema.primaryColor',
    errorColor: 'dsar.formStyling.schema.errorColor',
    fieldTextColor: 'dsar.formStyling.schema.fieldTextColor',
    helperTextColor: 'dsar.formStyling.schema.helperTextColor',
    backgroundColor: 'dsar.formStyling.schema.backgroundColor',
    formBackgroundColor: 'dsar.formStyling.schema.backgroundColor',
    asteriskColor: 'dsar.formStyling.schema.asteriskColor',
    requiredColor: 'dsar.formStyling.schema.asteriskColor',
};

// A change's `label` is the entity's own name. For default (un-customized)
// templates that name is itself an i18n key (e.g.
// "dsar.formTemplates.rejection.appealed.name"), so resolve it. Free-text names
// (custom fields, renamed templates) fall through unchanged.
// Key-shaped: only word characters/hyphens and dots (a single char class, no
// nested quantifier, so linear-time), then 3+ non-empty dot-separated segments.
// Requiring 2+ dots keeps single-dot free text like "acme.com" or "v1.2" from
// being treated as a key — translate() records missing keys in dev/ci, so a
// looser guard would pollute that list.
const KEY_CHARS = /^[a-z0-9_.-]+$/i;
const isKeyShaped = label => {
    if (typeof label !== 'string' || !KEY_CHARS.test(label)) {
        return false;
    }

    const segments = label.split('.');
    return segments.length >= 3 && segments.every(Boolean);
};
const resolveLabel = label => (isKeyShaped(label) ? translate(label) : label);

// The per-entity path families: `{prefix}{key}[.{attr}]`.
const ENTITY_PREFIXES = ['fields.', 'requestTypes.', 'templates.'];

// The entity's key and the property it reports. A key can hold dots of its own — a
// custom field is named after free text — so the change's `id` is preferred over
// splitting; events written without an id keep the positional read.
const splitEntityPath = (path, prefix, id) => {
    const rest = path.slice(prefix.length);
    const key = id === null || id === undefined ? '' : String(id);
    if (key && rest === key) {
        return { key, attr: null };
    }

    if (key && rest.startsWith(`${key}.`)) {
        return { key, attr: rest.slice(key.length + 1) };
    }

    const [first, second] = rest.split('.');
    return { key: first, attr: second ?? null };
};

// The property a change reports: for a per-entity path the attribute after the
// entity's key, else the last segment (`settings.captchaEnabled` → captchaEnabled).
const changeAttribute = ({ path = '', id } = {}) => {
    const prefix = ENTITY_PREFIXES.find(entityPrefix => path.startsWith(entityPrefix));

    return prefix ? splitEntityPath(path, prefix, id).attr : path.split('.').pop() || null;
};

// "{entity}: {property}", or the entity alone when the change is the entity itself.
// `propertyOnly` drops the entity, for a sentence that already names it.
const entityLabel = (path, prefix, { id, label, attrLabelKeys, propertyOnly }) => {
    const { key, attr } = splitEntityPath(path, prefix, id);
    const base = label || startCase(key);
    if (!attr) {
        return base;
    }

    const attrLabelKey = attrLabelKeys?.[attr];
    const property = attrLabelKey ? translate(attrLabelKey) : startCase(attr);

    return propertyOnly ? property : `${base}: ${property}`;
};

// A field's and a request type's properties, named the way the editor names them.
// A property with no entry keeps the startCased path segment, which for most reads
// the same ("Title", "Description") — an entry is what makes it localized too.
const FIELD_ATTR_LABEL_KEYS = {
    defaultValue: 'dsar.formFields.fields.defaultValue',
    displayOptions: 'dsar.formFields.fields.displayOptions.title',
    helperText: 'dsar.formFields.fields.helperText',
    label: 'common.fields.fieldLabel',
    // Field Name, the parameter the answer is recorded under — a separate property from
    // the Field Label the data subject sees, and editable, so a rename reports it.
    name: 'dsar.formFields.details.fieldName',
    placeholder: 'dsar.formFields.fields.placeholder',
    position: 'common.order',
    readOnlyValue: 'dsar.formFields.fields.readOnlyValue',
    required: 'common.required',
    type: 'common.fields.type',
};

const REQUEST_TYPE_ATTR_LABEL_KEYS = {
    agentSubmissionAllowed: 'dsar.formRequestTypes.agentVerificationEnabled.label',
    agentVerificationLevel: 'dsar.formRequestTypes.agentVerificationLevel.label',
    description: 'dsar.formRequestTypes.requestDescription.label',
    instructions: 'dsar.formRequestTypes.requestInstructions.label',
    jurisdiction: 'dsar.formRequestTypes.jurisdictions.label',
    ordinal: 'common.order',
    title: 'dsar.formRequestTypes.requestTitle.label',
    verificationLevel: 'dsar.formRequestTypes.verificationLevel.label',
};

// A template's properties, named the way that type's editor names them, so the log
// reads like the dialog the admin used. The change carries the template `type`; an
// unknown type or property — and older events, which carry no type — falls back to
// the startCased path segment.
const TEMPLATE_ATTR_LABEL_KEYS = {
    EMAIL: {
        header: 'dsar.templates.editEmailDialog.email.header.label',
        subject: 'dsar.templates.editEmailDialog.email.subject.label',
    },
    PORTAL: {
        body: 'dsar.templates.editPortalDialog.portal.body.label',
        name: 'dsar.templates.editPortalDialog.portal.name.label',
    },
    WEB: {
        footer: 'dsar.templates.editWebDialog.web.footer.label',
        header: 'dsar.templates.editWebDialog.web.header.label',
    },
};

// Human label for a change: a translated label for known setting/styling paths
// and palette colours, an item's own `label` for fields plus the changed
// property, or a startCased path segment as a fallback.
const labelForChange = (
    { path = '', id, label: rawLabel, type } = {},
    { propertyOnly = false } = {}
) => {
    const label = resolveLabel(rawLabel);
    if (PATH_LABEL_KEYS[path]) {
        return translate(PATH_LABEL_KEYS[path]);
    }

    if (path.startsWith('styling.palette.')) {
        const colour = path.slice('styling.palette.'.length);
        return PALETTE_LABEL_KEYS[colour]
            ? translate(PALETTE_LABEL_KEYS[colour])
            : startCase(colour);
    }

    if (path.startsWith('fields.')) {
        return entityLabel(path, 'fields.', {
            id,
            label,
            attrLabelKeys: FIELD_ATTR_LABEL_KEYS,
            propertyOnly,
        });
    }

    if (path.startsWith('requestTypes.')) {
        return entityLabel(path, 'requestTypes.', {
            id,
            label,
            attrLabelKeys: REQUEST_TYPE_ATTR_LABEL_KEYS,
            propertyOnly,
        });
    }

    if (path.startsWith('templates.')) {
        // Template properties are named per type: a portal "name" is its Message Name.
        return entityLabel(path, 'templates.', {
            id,
            label,
            attrLabelKeys: TEMPLATE_ATTR_LABEL_KEYS[type],
            propertyOnly,
        });
    }

    const segments = path.split('.');
    return label || startCase(segments[segments.length - 1]);
};

// Stored form values are HTML-encoded (an apostrophe as &apos;, etc.). Decode
// them so they render as text — matching how the resource name renders — before
// re-escaping for the [html] block below. unescape runs first (it does not
// handle &apos;), then &apos; is normalized, so a double-encoded &amp;apos; still
// resolves to an apostrophe.
const decodeEntities = value => unescape(String(value)).replace(/&apos;/gi, "'");

// Values go into a BBCode [html] block, so escape HTML and BBCode brackets to
// keep a value like "</html]" or markup from closing the block early.
const escapeValue = value =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\[/g, '&#91;')
        .replace(/]/g, '&#93;');

// Same, plus quote-escaping for use inside an HTML attribute (the title tooltip).
const escapeAttr = value => escapeValue(value).replace(/"/g, '&quot;');

const TRUNCATE_AT = 80;

// Collection values arrive as string arrays or, for orgs, { label, value }
// pairs — show the label (name) for those.
const asText = item => (item && typeof item === 'object' ? item.label ?? item.value ?? '' : item);

// One chip of a row: the value, and the full text as a tooltip when it was cut.
const chip = (style, { display, title } = {}) =>
    `<span style="${style}"${title ? ` title="${title}"` : ''}>${display}</span>`;

// A change value as { display, title }: the decoded value truncated for display,
// keeping the full value in `title` (a hover tooltip) when truncated so long
// text stays inspectable. `name` renders one item of it, and defaults to naming
// nothing — a caller whose values are stored enums passes its own.
const renderValue = (value, name = asText) => {
    if (value === null || value === undefined || value === '') {
        return { display: '—', title: null };
    }

    const text = decodeEntities(Array.isArray(value) ? value.map(name).join(', ') : name(value));
    if (text.length > TRUNCATE_AT) {
        return {
            display: escapeValue(`${text.slice(0, TRUNCATE_AT)}…`),
            title: escapeAttr(text),
        };
    }

    return { display: escapeValue(text), title: null };
};

// UI labels for a field's Display Options state (the backend sends a stable token).
const DISPLAY_OPTION_LABEL_KEYS = {
    all: 'dsar.formFields.fields.displayOptions.enabledAll',
    specific: 'dsar.formFields.fields.displayOptions.hasRequestConditions',
    disabled: 'dsar.formFields.fields.displayOptions.disabled',
};

// A field's Display Options change value: { option, requestTypes }. Render the
// option in UI language (localized), appending the selected request-type names for
// "specific". Falls back to renderValue for anything unexpected.
const renderDisplayOption = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return renderValue(value);
    }

    const { option, requestTypes = [] } = value;
    const optionLabel = DISPLAY_OPTION_LABEL_KEYS[option]
        ? translate(DISPLAY_OPTION_LABEL_KEYS[option])
        : startCase(String(option ?? ''));
    const names = requestTypes.map(name => decodeEntities(String(name)));
    const text =
        option === 'specific' && names.length ? `${optionLabel}: ${names.join(', ')}` : optionLabel;

    if (text.length > TRUNCATE_AT) {
        return {
            display: escapeValue(`${text.slice(0, TRUNCATE_AT)}…`),
            title: escapeAttr(text),
        };
    }

    return { display: escapeValue(text), title: null };
};

// Stored value → i18n key for the properties that hold an enum, keyed by property so
// free text that reads like a stored state is never renamed.
const VALUE_LABEL_KEYS_BY_ATTR = {
    agentVerificationLevel: DSAR_REQUEST_TYPE_AGENT_VERIFICATION_LEVEL_LABELS,
    jurisdiction: DSAR_REQUEST_TYPE_JURISDICTION_LABELS,
    type: DSAR_FIELD_TYPE_LABELS,
    verificationLevel: DSAR_REQUEST_TYPE_VERIFICATION_LEVEL_LABELS,
};

// A toggle reads as the state it leaves the setting in, as the workflow log does.
const BOOLEAN_LABEL_KEYS = { false: 'common.disabled', true: 'common.enabled' };

// A custom field's Field Name is stored with the editor's `o-` prefix, which the
// editor itself hides on the way in and out (FieldDetails.component). Only field
// paths: a template's `name` is free text an admin typed, prefix and all.
const FIELD_NAME_PREFIX = /^o-/;
const isFieldName = (attr, path) =>
    attr === 'name' && typeof path === 'string' && path.startsWith('fields.');

// Names one value of a form change: a boolean as the state it sets, an enum through
// its editor's own label, anything else as the text it already is.
const nameFormValue = (attr, path) => value => {
    if (typeof value === 'boolean') {
        return translate(BOOLEAN_LABEL_KEYS[String(value)]);
    }

    if (isFieldName(attr, path) && typeof value === 'string') {
        return asText(value.replace(FIELD_NAME_PREFIX, ''));
    }

    const labelKey = VALUE_LABEL_KEYS_BY_ATTR[attr]?.[String(value)];

    return labelKey ? translate(labelKey) : asText(value);
};

// Every i18n key a change label or value can resolve to, for the test that proves
// none of them is a typo.
export const CHANGE_LABEL_KEYS = [
    ...Object.values(PATH_LABEL_KEYS),
    ...Object.values(PALETTE_LABEL_KEYS),
    ...Object.values(FIELD_ATTR_LABEL_KEYS),
    ...Object.values(REQUEST_TYPE_ATTR_LABEL_KEYS),
    ...Object.values(TEMPLATE_ATTR_LABEL_KEYS).flatMap(labels => Object.values(labels)),
    ...Object.values(VALUE_LABEL_KEYS_BY_ATTR).flatMap(labels => Object.values(labels)),
    ...Object.values(BOOLEAN_LABEL_KEYS),
    ...Object.values(DISPLAY_OPTION_LABEL_KEYS),
];

// Surfaces the source form's name for the %{sourceFormName} placeholder. A copied
// form's primary resource is the new form, so the source is carried in
// metadata.sourceFormName. Form names are stored HTML-encoded, so decode entities
// (as the change formatter does) to avoid rendering e.g. "Charlie&apos;s Form".
// Returns a non-empty '—' when absent so translateObject always replaces the
// placeholder.
export const sourceFormNameFormatter = (_value, auditEventData = {}) => {
    const name = auditEventData?.metadata?.sourceFormName;

    return name ? stripBBCodeTags(decodeEntities(name)) : '—';
};

// metadata.changes[] as a list: struck-through "old" → "new" chips for a MODIFIED
// change, one chip for ADDED/REMOVED. Returns '—' rather than an empty string when
// there is nothing, so translateObject always replaces the placeholder.
const renderChanges = (changes, { propertyOnly = false } = {}) => {
    if (!Array.isArray(changes) || changes.length === 0) {
        return '—';
    }

    const listItems = changes
        .map(change => {
            const { action, before, after } = change || {};
            const label = `<b>${escapeValue(
                decodeEntities(labelForChange(change, { propertyOnly }))
            )}</b>`;
            // Display Options carry a { option, requestTypes } value rendered in UI
            // language; every other value is named after the property it belongs to,
            // so a stored boolean or enum reads as the editor words it.
            const render = change?.path?.endsWith('.displayOptions')
                ? renderDisplayOption
                : changeValue =>
                      renderValue(
                          changeValue,
                          nameFormValue(changeAttribute(change), change?.path)
                      );

            if (action === 'ADDED') {
                return `<li style="${ITEM_STYLE}">${label}&nbsp;&nbsp;${chip(NEW_CHIP, render(after))}</li>`;
            }

            if (action === 'REMOVED') {
                return `<li style="${ITEM_STYLE}">${label}&nbsp;&nbsp;${chip(OLD_CHIP, render(before))}</li>`;
            }

            return `<li style="${ITEM_STYLE}">${label}&nbsp;&nbsp;${chip(
                OLD_CHIP,
                render(before)
            )}${chip(ARROW, { display: '→' })}${chip(NEW_CHIP, render(after))}</li>`;
        })
        .join('');
    return `[html]<ul style="${LIST_STYLE}">${listItems}</ul>[/html]`;
};

// The changes an event carries; an array may also be passed directly, for reuse.
const changesOf = (value, auditEventData) =>
    Array.isArray(value) ? value : auditEventData?.metadata?.changes;

export const changeFormatter = (value, auditEventData = {}) =>
    renderChanges(changesOf(value, auditEventData));

// A created template's sentence already says a template was added, so its rows name
// the property alone rather than repeating the template's name on each one.
export const templateCreatedChangeFormatter = (value, auditEventData = {}) =>
    renderChanges(changesOf(value, auditEventData), { propertyOnly: true });

// A deletion names the template in its sentence rather than striking its name
// through in a chip, so the name is read off the change the event carries.
export const templateNameFormatter = (_value, auditEventData = {}) => {
    const [{ label } = {}] = auditEventData?.metadata?.changes ?? [];

    // The name lands in the sentence, where the renderer parses BBCode, so anything
    // that would read as a tag is dropped.
    return label ? stripBBCodeTags(decodeEntities(resolveLabel(label))) : '—';
};

/* --- Workflow node events ----------------------------------------------------------------
 * A node event reports the same `metadata.changes[]` as a form event, and renders as the same
 * rows. It needs two things a form change does not: a stored config value is an enum that means
 * nothing on its own — DELETE is a request type, `equals` an operator — and a few fields hold a
 * whole object, which reads as a sub-list of its keys.
 */

/**
 * Stored value → i18n key, for every enum a node config holds: the field a rule tests, the
 * operator it tests with, the request types and actions of an actionConfig, and the booleans.
 * The editor's own tables are reused verbatim, so rewording one carries into the log.
 */
const WORKFLOW_VALUE_LABEL_KEYS = {
    ...mapValues(DECISION_FIELDS_BY_PATH, 'label'),
    ...mapValues(OPERATORS_BY_VALUE, 'label'),
    // The static options a decision field offers: statuses, jurisdictions, origins.
    ...Object.assign(
        {},
        ...Object.values(DECISION_FIELDS_BY_PATH).map(field =>
            Object.fromEntries(
                (field.staticOptions ?? []).map(option => [option.value, option.label])
            )
        )
    ),
    ...DSAR_REQUEST_TYPE_LABELS,
    ...DSAR_REQUEST_TYPE_GROUP_LABELS,
    ...DSAR_ACTION_LABELS,
    true: 'common.enabled',
    false: 'common.disabled',
};

/**
 * Names a stored value. The vocabulary above names an enum. A custom form field is named by its
 * own path instead, because the field belongs to the customer's form: the path carries the
 * inputName the form gave it, behind an `o-` prefix, and the dialog names it the same way. A
 * value neither knows keeps its own text.
 */
const getChangeValue = value => {
    if (Array.isArray(value)) {
        return value.map(getChangeValue).join(', ');
    }
    const fieldName = typeof value === 'string' ? getCustomFieldLabelFromPath(value) : null;

    return translateOr(WORKFLOW_VALUE_LABEL_KEYS[String(value)], fieldName ?? value);
};

/**
 * Names the key of an object value. A key is an identifier rather than text a customer typed,
 * so one the vocabulary does not know is start-cased instead of shown raw.
 */
const getChangeKey = key => translateOr(WORKFLOW_VALUE_LABEL_KEYS[key], startCase(key));

/**
 * Names one item of a node config. A branch is the condition it applies and the value it matches
 * — "is Delete" — where a single value is stored as `value` and a multi-value operator's as
 * `values`. Reading those two keys rather than the whole object keeps any other key the producer
 * sends out of the log, and leaves out the field the branch tests: a switch keeps that at config
 * level, where it reads as its own row, and every rule of a decision repeats the one field the
 * decision branches on. Every other value is named above.
 */
const nameConfigValue = item => {
    if (item === null || typeof item !== 'object' || isOptionObject(item)) {
        return getChangeValue(asText(item));
    }
    const { operator, value, values } = item;

    return [operator, value ?? values]
        .filter(part => part !== undefined)
        .map(getChangeValue)
        .join(' ');
};

/** One row: the field, then the value it holds now, after the value it replaced. */
const renderChangeRow = (label, before, after) => {
    const value = side => renderValue(side, nameConfigValue);
    // Nothing to strike through: the field held nothing before, or it did not change.
    const chips =
        before === null || before === undefined || isEqual(before, after)
            ? chip(NEW_CHIP, value(after))
            : chip(OLD_CHIP, value(before)) +
              chip(ARROW, { display: '\u2192' }) +
              chip(NEW_CHIP, value(after));

    return `<li style="${ITEM_STYLE}"><b>${label}</b>&nbsp;&nbsp;${chips}</li>`;
};

/**
 * The fields that hold an internal id. They mean nothing to a reader and change whenever the
 * node is saved, so no event shows them.
 */
const HIDDEN_FIELDS = new Set([
    'config.approvalId',
    'config.priority',
    'config.processingType',
    'config.rejectionReasonCode',
    'config.taskId',
]);

// A field names the previous step's outcome when its last segment is the marker. The marker is
// bare until the parent edge is wired, and `parents.<parentId>.` prefixed after.
const isOutcomePath = path =>
    typeof path === 'string' && path.split('.').pop() === PREVIOUS_OUTCOME_FIELD;

/**
 * The marker as a change's own value — a switch reports the field it routes on by itself — as the
 * field of one rule, or as the field of any rule of a set.
 */
const carriesOutcomeMarker = value => {
    if (Array.isArray(value)) {
        return value.some(carriesOutcomeMarker);
    }

    if (value !== null && typeof value === 'object') {
        return isOutcomePath(value.path);
    }

    return isOutcomePath(value);
};

/**
 * Whether the node branches on whether the previous step succeeded. The marker is read from the
 * value rather than from the path, so the basis shows wherever the producer nests the rule.
 *
 * @param {Array} changes - the event's metadata.changes[]
 * @returns {boolean} true when the node branches on the previous step's outcome
 */
const isPreviousOutcome = changes =>
    changes.some(
        change => carriesOutcomeMarker(change?.before) || carriesOutcomeMarker(change?.after)
    );

/**
 * The first sentence of each node event. Keyed by the event types of ./constants PLATFORM_EVENTS,
 * as literals: reading that file here would make the two modules a cycle.
 */
const WORKFLOW_NODE_SENTENCE_KEYS = {
    'platform.workflowNodeCreated': 'workflows.audit.nodeCreated',
    'platform.workflowNodeUpdated': 'workflows.audit.nodeUpdated',
    'platform.workflowNodeDeleted': 'workflows.audit.nodeDeleted',
};

/**
 * The same sentence, with the changes hung off the end of it. A deletion has no changes — the node
 * is gone — so it keeps the plain sentence above, and an event that reports none takes it too,
 * rather than ending on an empty "Changes:".
 */
const WORKFLOW_NODE_SENTENCE_WITH_CHANGES_KEYS = {
    'platform.workflowNodeCreated': 'workflows.audit.nodeCreatedWithChanges',
    'platform.workflowNodeUpdated': 'workflows.audit.nodeUpdatedWithChanges',
};

/**
 * Shows one `platform.workflowNode*` event: "Step <node name || node type> was updated by <user>.
 * Changes:", then a row per changed field. It takes the complete event, because the event type
 * gives the verb (refer to helpers/AuditEvents).
 */
export const workflowNodeChangeFormatter = ({
    auditEventData = {},
    auditEventType,
    auditUser,
} = {}) => {
    const { metadata, name, nodeType } = auditEventData;

    // A node with no name of its own is named by its type.
    const step = name || NODE_TYPE_CONFIG[nodeType]?.label;
    const changes = Array.isArray(metadata?.changes) ? metadata.changes : [];

    // A branching node's config holds nothing but the field it routes on and the rules that test
    // it, so a node on the previous step's outcome states that fixed split — which the user never
    // picked — and shows no row at all.
    const outcomeBasis =
        isBranchingNodeType(nodeType) && isPreviousOutcome(changes)
            ? translate('workflows.audit.previousOutcomeBasis')
            : '';

    const rows = (outcomeBasis ? [] : changes)
        .filter(change => change?.path && !HIDDEN_FIELDS.has(change.path))
        .map(({ after, before, path }) => {
            const label = escapeValue(labelForChange({ path }));

            // A field that holds an object reads as a sub-list of the keys that changed — an
            // actionConfig as its request types — and every other field as one row.
            if (!isFlatObject(before) && !isFlatObject(after)) {
                return renderChangeRow(label, before, after);
            }

            const previous = isFlatObject(before) ? before : {};
            const next = isFlatObject(after) ? after : {};
            const keyRows = unionKeys(previous, next)
                // An unchanged key is not a change. A new node has an empty previous side, so
                // each of its keys shows.
                .filter(key => !isEqual(previous[key], next[key]))
                .map(key =>
                    renderChangeRow(escapeValue(getChangeKey(key)), previous[key], next[key])
                )
                .join('');

            // No child row means that no key changed, so the field shows nothing at all.
            return keyRows
                ? `<li style="${ITEM_STYLE}"><b>${label}</b>` +
                      `<ul style="${NESTED_LIST_STYLE}">${keyRows}</ul></li>`
                : '';
        })
        .join('');
    const list = rows ? `<ul style="${LIST_STYLE}">${rows}</ul>` : '';

    // Step <node name || node type> was updated by <user>. Changes: <rows>
    const changesKey =
        list && !outcomeBasis
            ? WORKFLOW_NODE_SENTENCE_WITH_CHANGES_KEYS[auditEventType]
            : undefined;
    const sentence = translate(changesKey ?? WORKFLOW_NODE_SENTENCE_KEYS[auditEventType], {
        step: `<b>${renderValue(translate(step ?? 'common.unknown')).display}</b>`,
        user: `<b>${auditUser ? escapeValue(auditUser) : translate('common.unknown')}</b>`,
        changes: list,
    });

    // A sentence that takes no changes still shows any it was given, after itself.
    return `[html]${sentence}${outcomeBasis ? `. ${outcomeBasis}` : ''}${
        changesKey ? '' : list
    }[/html]`;
};
